import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useCallback, useEffect, useState } from 'react'
import {
  $getSelection,
  $isDecoratorNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_NORMAL,
  FORMAT_TEXT_COMMAND,
  KEY_MODIFIER_COMMAND,
  LexicalEditor,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical'
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils'
import { getSelectedNode, isDecoratorNodeSelected, sanitizeUrl } from './utils'
import { $isListNode, ListNode } from '@lexical/list'
import { $isHeadingNode } from '@lexical/rich-text'
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'

export type BlockType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'bullet' | 'number' | 'paragraph' | 'quote'

const BLOCK_TYPE_TO_BLOCK_NAME: { [key in BlockType]: string } = {
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  bullet: 'Bullet List',
  number: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote',
}

export interface RichTextFormatting {
  selectedBlockType: keyof typeof BLOCK_TYPE_TO_BLOCK_NAME
  isBold: boolean
  isItalic: boolean
  isUnderline: boolean
  isStrikethrough: boolean
  formatBold: () => void
  formatItalic: () => void
  formatUnderline: () => void
  formatStrikethrough: () => void
  isLink: boolean
  setIsLink: (isLink: boolean) => void
  isLinkEditMode: boolean
  setIsLinkEditMode: (isLinkEditMode: boolean) => void
  insertLink: () => void
}

export interface RichTextUndoRedo {
  canUndo: boolean
  canRedo: boolean
  undo: () => void
  redo: () => void
}

export interface RichTextToolbar {
  editor: LexicalEditor
  formatting: RichTextFormatting
  undoRedo: RichTextUndoRedo
}

export const useRichTextToolbar = (): RichTextToolbar => {
  const [editor] = useLexicalComposerContext()
  const [activeEditor, setActiveEditor] = useState<LexicalEditor>(editor)
  const [selectedBlockType, setSelectedBlockType] = useState<BlockType>('paragraph')
  const [isLink, setIsLink] = useState<boolean>(false)
  const [isBold, setIsBold] = useState<boolean>(false)
  const [isItalic, setIsItalic] = useState<boolean>(false)
  const [isUnderline, setIsUnderline] = useState<boolean>(false)
  const [isStrikethrough, setIsStrikethrough] = useState<boolean>(false)
  const [canUndo, setCanUndo] = useState<boolean>(false)
  const [canRedo, setCanRedo] = useState<boolean>(false)
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false)

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent()
              return parent !== null && $isRootOrShadowRoot(parent)
            })

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow()
      }

      const elementKey = element.getKey()
      const elementDOM = activeEditor.getElementByKey(elementKey)

      // Update text format
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))

      // Update links
      const node = getSelectedNode(selection)
      const parent = node.getParent()
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true)
      } else {
        setIsLink(false)
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode)
          const type = parentList ? parentList.getListType() : element.getListType()
          if (type !== 'check') {
            setSelectedBlockType(type)
          }
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType()
          if (type in BLOCK_TYPE_TO_BLOCK_NAME) {
            setSelectedBlockType(type as BlockType)
          }
        }
      }
    }
  }, [activeEditor])

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        $updateToolbar()
        setActiveEditor(newEditor)
        return false
      },
      COMMAND_PRIORITY_CRITICAL,
    )
  }, [editor, $updateToolbar])

  useEffect(() => {
    return mergeRegister(
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar()
        })
      }),
      activeEditor.registerCommand<boolean>(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload)
          return false
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      activeEditor.registerCommand<boolean>(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload)
          return false
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    )
  }, [$updateToolbar, activeEditor, editor])

  useEffect(() => {
    return activeEditor.registerCommand(
      KEY_MODIFIER_COMMAND,
      (payload) => {
        const event: KeyboardEvent = payload
        const { code, ctrlKey, metaKey } = event

        if (code === 'KeyK' && (ctrlKey || metaKey)) {
          event.preventDefault()
          let url: string | null
          if (!isLink) {
            setIsLinkEditMode(true)
            url = sanitizeUrl('https://')
          } else {
            setIsLinkEditMode(false)
            url = null
          }
          return activeEditor.dispatchCommand(
            TOGGLE_LINK_COMMAND,
            url
              ? {
                  url,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                }
              : null,
          )
        }
        return false
      },
      COMMAND_PRIORITY_NORMAL,
    )
  }, [activeEditor, isLink, setIsLinkEditMode])

  const formatBold = useCallback(() => {
    activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
  }, [activeEditor])

  const formatItalic = useCallback(() => {
    activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
  }, [activeEditor])

  const formatUnderline = useCallback(() => {
    activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
  }, [activeEditor])

  const formatStrikethrough = useCallback(() => {
    activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
  }, [activeEditor])

  const insertLink = useCallback(() => {
    if (isDecoratorNodeSelected(editor)) {
      return
    }
    if (!isLink) {
      setIsLinkEditMode(true)
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
        url: sanitizeUrl('https://'),
        target: '_blank',
        rel: 'noopener noreferrer',
      })
    } else {
      setIsLinkEditMode(false)
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    }
  }, [editor, isLink, setIsLinkEditMode])

  const undo = useCallback(() => {
    activeEditor.dispatchCommand(UNDO_COMMAND, undefined)
  }, [activeEditor])

  const redo = useCallback(() => {
    activeEditor.dispatchCommand(REDO_COMMAND, undefined)
  }, [activeEditor])

  return {
    editor: activeEditor,
    formatting: {
      selectedBlockType,
      isBold,
      isItalic,
      isUnderline,
      isStrikethrough,
      formatBold,
      formatItalic,
      formatUnderline,
      formatStrikethrough,
      isLink,
      setIsLink,
      isLinkEditMode,
      setIsLinkEditMode,
      insertLink,
    },
    undoRedo: {
      canUndo,
      canRedo,
      undo,
      redo,
    },
  }
}
